import { useMemo, useState, useRef, useCallback, useEffect, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

import { AlignmentTool } from '@/components/calendar/AlignmentTool';
import { CycleWizard } from '@/components/calendar/CycleWizard';
import { RingThemeWizard } from '@/components/calendar/RingThemeWizard';
import { Dashboard } from '@/components/dashboard/Dashboard';
import type { WidgetConfig } from '@/components/dashboard/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Clock, Plus, Trash2, Settings, EllipsisVertical, LayoutGrid, PanelsTopLeft, RefreshCw, Clapperboard } from 'lucide-react';
import { format } from 'date-fns';
import { MONTH_NAMES } from '@/lib/calendar13Types';
import { gregorianToDay13 } from '@/lib/calendar13';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

import type { CycleRing } from '@/lib/cycleTypes';
import type { CycleAlignment } from '@/lib/cycleConfig';
import type { RingTheme, RingThemeState } from '@/lib/ringThemeStore';
import type { DashboardView } from '@/lib/dashboardTypes';

export function DashboardPageView({
  isLoggedIn,
  canEditDashboard,
  requiresBetaAccess,
  metaLoaded,
  dashboardStateReady,
  betaEnabled,
  betaDashboardsVisible,
  navItems,
  showCycleWizard,
  setShowCycleWizard,
  showAlignmentTool,
  setShowAlignmentTool,
  showRingThemeWizard,
  setShowRingThemeWizard,
  alignmentInitialCycleId,
  setAlignmentInitialCycleId,
  currentGregorianDate,
  isManualMode,
  isNowLocked,
  handleSyncToNow,
  dayOfYear,
  dashboardViews,
  activeViewId,
  setActiveViewId,
  activeView,
  handleCreateView,
  handleDeleteView,
  activeWidgets,
  addableWidgets,
  handleRemoveWidget,
  handleAddWidget,
  handleSaveDashboard,
  dashboardStorageKey,
  isMobileBreakpoint,
  cycles,
  hiddenRings,
  onToggleRing,
  onRingsChange,
  onRotationChangeFromCycles,
  alignments,
  ringThemeState,
  onSaveTheme,
  onDeleteTheme,
  onApplyThemeToRing,
  onApplyThemeLayout,
  onApplyThemeOrder,
  cycleDisplayMode,
  onCycleDisplayModeChange,
  onAlign,
  onApplyAlignment,
  onImportCycles,
  onCreateCycle,
  onUpdateCycle,
  onDeleteCycle,
  editingCycle,
  onCloseCycleWizard,
  firestoreDisabled,
  widgetSyncState,
  setWidgetSyncState,
  onSyncModesChange,
  onToggleAllWidgets,
  onSignOut,
}: {
  isLoggedIn: boolean;
  canEditDashboard: boolean;
  requiresBetaAccess: boolean;
  metaLoaded: boolean;
  dashboardStateReady: boolean;
  betaEnabled: boolean;
  betaDashboardsVisible: boolean;
  navItems: Array<{ id: string; label: string; icon: ReactNode; to: string }>;

  showCycleWizard: boolean;
  setShowCycleWizard: (open: boolean) => void;
  showAlignmentTool: boolean;
  setShowAlignmentTool: (open: boolean) => void;
  showRingThemeWizard: boolean;
  setShowRingThemeWizard: (open: boolean) => void;
  alignmentInitialCycleId: string | undefined;
  setAlignmentInitialCycleId: (value: string | undefined) => void;

  currentGregorianDate: Date;
  isManualMode: boolean;
  isNowLocked: boolean;
  handleSyncToNow: () => void;
  dayOfYear: number;

  dashboardViews: DashboardView[];
  activeViewId: string;
  setActiveViewId: (id: string) => void;
  activeView: DashboardView | null;
  handleCreateView: () => void;
  handleDeleteView: () => void;

  activeWidgets: WidgetConfig[];
  addableWidgets: WidgetConfig[];
  handleRemoveWidget: (id: string) => void;
  handleAddWidget: (id: string) => void;
  handleSaveDashboard: () => void;
  dashboardStorageKey: string;
  isMobileBreakpoint: boolean;

  cycles: CycleRing[];
  hiddenRings: string[];
  onToggleRing: (ringId: string) => void;
  onRingsChange: (next: CycleRing[]) => void;
  onRotationChangeFromCycles: (dayOfYear: number, fractionOfDay: number) => void;
  alignments: CycleAlignment[];

  ringThemeState: RingThemeState;
  onSaveTheme: (theme: RingTheme) => void;
  onDeleteTheme: (themeId: string) => void;
  onApplyThemeToRing: (themeId: string, ringId: string) => void;
  onApplyThemeLayout: (themeId: string) => void;
  onApplyThemeOrder: (themeId: string) => void;
  cycleDisplayMode: 'single' | 'multi';
  onCycleDisplayModeChange: (mode: 'single' | 'multi') => void;

  onAlign: (alignment: CycleAlignment) => void;
  onApplyAlignment: (cycles: CycleRing[]) => void;

  onImportCycles: (imported: CycleRing[]) => void;
  onCreateCycle: (cycle: CycleRing) => void;
  onUpdateCycle: (cycle: CycleRing) => void;
  onDeleteCycle: (cycleId: string) => void;
  editingCycle: CycleRing | null;
  onCloseCycleWizard: () => void;
  firestoreDisabled: boolean;
  widgetSyncState?: 'all-linked' | 'all-unlinked' | 'partial';
  setWidgetSyncState?: (state: 'all-linked' | 'all-unlinked' | 'partial') => void;
  onSyncModesChange?: (syncModes: Record<string, 'linked' | 'isolated' | 'fixed'>) => void;
  onToggleAllWidgets?: () => void;
  onSignOut?: () => void;
}) {
  type DashboardViewMode = 'skin' | 'grid' | 'pager';
  const safeDashboardViews = useMemo(
    () => (Array.isArray(dashboardViews) ? dashboardViews.filter((view) => view && view.id) : []),
    [dashboardViews]
  );
  const [dashboardViewMode, setDashboardViewMode] = useState<DashboardViewMode>('skin');
  const [is24hr, setIs24hr] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Store the toggle all widgets function from Dashboard
  const toggleAllWidgetsRef = useRef<((checked?: boolean) => void) | null>(null);
  const handleToggleAllWidgets = useCallback((checked: boolean) => {
    toggleAllWidgetsRef.current?.(checked);
  }, []);

  // Header should be visible in grid and pager modes, only hidden in skin mode
  const isSkinMode = dashboardViewMode === 'skin';
  const inlineHeaderInSettingsPage = isMobileBreakpoint && dashboardViewMode === 'pager';
  const isSkinRoute = location.pathname.startsWith('/dashboard/skin');

  useEffect(() => {
    if (!isSkinRoute || !isSkinMode || !activeViewId) return;
    const targetPath = `/dashboard/skin/${activeViewId}`;
    if (location.pathname !== targetPath) {
      navigate(targetPath, { replace: true });
    }
  }, [activeViewId, isSkinMode, isSkinRoute, location.pathname, navigate]);

  const mobilePagerSettingsHeader = useMemo(
    () => (
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Dashboard view</p>
            <Select value={activeViewId} onValueChange={setActiveViewId}>
              <SelectTrigger className="w-full bg-card/60 backdrop-blur border-border/60">
                <SelectValue placeholder="Choose a view" />
              </SelectTrigger>
              <SelectContent className="bg-card/90 backdrop-blur border-border/60">
                {safeDashboardViews.map((view) => (
                    <SelectItem key={view.id} value={view.id}>
                      {view.name ?? 'Dashboard'}
                    </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {canEditDashboard && (
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={handleCreateView}>
                Save as new view
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={handleDeleteView} disabled={!activeView}>
                Delete view
              </Button>
            </div>
          )}
        </div>


      </div>
    ),
    [
      activeView,
      activeViewId,
      safeDashboardViews,
      handleCreateView,
      handleDeleteView,
      canEditDashboard,
      setActiveViewId,
    ]
  );

  if (requiresBetaAccess && !metaLoaded) {
    return (
      <div className="min-h-screen bg-background nebula-bg star-field flex items-center justify-center px-4">
        <div className="text-center space-y-3">
          <p className="text-sm uppercase tracking-[0.3em] text-muted-foreground">Loading</p>
          <h1 className="text-2xl font-display font-semibold">Checking beta access…</h1>
        </div>
      </div>
    );
  }

  if (requiresBetaAccess && !betaEnabled) {
    return (
      <div className="min-h-screen bg-background nebula-bg star-field flex items-center justify-center px-4">
        <div className="max-w-lg text-center space-y-4 bg-card/70 border border-border/60 rounded-2xl p-8 backdrop-blur">
          <p className="text-sm uppercase tracking-[0.3em] text-muted-foreground">Beta Locked</p>
          <h1 className="text-2xl font-display font-semibold">Beta access is currently disabled.</h1>
          <p className="text-sm text-muted-foreground">
            Ask an administrator to enable beta access from the developer page before viewing.
          </p>
          <Button asChild variant="outline">
            <Link to="/landing">Back to landing</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (requiresBetaAccess && !betaDashboardsVisible) {
    return (
      <div className="min-h-screen bg-background nebula-bg star-field flex items-center justify-center px-4">
        <div className="max-w-lg text-center space-y-4 bg-card/70 border border-border/60 rounded-2xl p-8 backdrop-blur">
          <p className="text-sm uppercase tracking-[0.3em] text-muted-foreground">Dashboards Hidden</p>
          <h1 className="text-2xl font-display font-semibold">Beta dashboards are not shared yet.</h1>
          <p className="text-sm text-muted-foreground">
            Admins can enable global dashboard visibility for beta users from the developer page.
          </p>
          <Button asChild variant="outline">
            <Link to="/landing">Back to landing</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (!dashboardStateReady) {
    return (
      <div className="h-[100dvh] bg-background nebula-bg star-field flex items-center justify-center px-4">
        <div className="text-center space-y-3">
          <p className="text-sm uppercase tracking-[0.3em] text-muted-foreground">Loading</p>
          <h1 className="text-2xl font-display font-semibold">Syncing timeline…</h1>
          <p className="text-sm text-muted-foreground">Waiting for the saved timeline and cycle state before rendering.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background nebula-bg star-field flex flex-col overflow-hidden" style={{ height: 'var(--app-height, 100dvh)', maxHeight: 'var(--app-height, 100dvh)' }}>

      {/* Timeline header bar */}
      {(() => {
        const headerDay13 = gregorianToDay13(currentGregorianDate);
        const safeMoon = Math.min(Math.max(1, headerDay13.month), 13);
        const moonName = headerDay13.isOutOfTime
          ? 'Day Out of Time'
          : (MONTH_NAMES[safeMoon - 1]?.name ?? `Moon ${safeMoon}`);
        const gregStr = is24hr
          ? format(currentGregorianDate, 'MMM d, yyyy · HH:mm:ss')
          : format(currentGregorianDate, 'MMM d, yyyy · h:mm:ss aa');
        const mayanStr = headerDay13.isOutOfTime ? 'Day Out of Time' : `${moonName} · Month ${safeMoon} of 13`;
        return (
          <div className="shrink-0 flex items-center justify-between gap-4 px-4 pb-1.5 bg-black/40 backdrop-blur border-b border-white/8 text-[11px] text-white/60 font-mono tracking-wide z-50" style={{ paddingTop: 'max(0.375rem, env(safe-area-inset-top, 0px))' }}>
            <div className="shrink-0 flex items-center gap-1.5">
              <span className="text-white/60">{gregStr}</span>
              <button
                type="button"
                onClick={() => setIs24hr(p => !p)}
                className="rounded px-1 py-0.5 text-[9px] font-bold tracking-wider transition-colors bg-white/10 text-white/50 hover:bg-white/18 hover:text-white/80"
              >{is24hr ? '12H' : '24H'}</button>
            </div>
            <span className={cn('shrink-0 font-semibold', isManualMode ? 'text-purple-300' : 'text-white/50')}>
              {mayanStr}
            </span>
            <button
              type="button"
              onClick={handleSyncToNow}
              title={isNowLocked ? 'Unlock timeline' : 'Lock to now'}
              className={cn(
                'shrink-0 flex items-center gap-1 rounded px-1.5 py-0.5 transition-colors',
                isNowLocked
                  ? 'text-emerald-300 hover:text-emerald-100 hover:bg-emerald-500/20'
                  : isManualMode
                    ? 'text-purple-300 hover:text-white hover:bg-white/10'
                    : 'text-white/30 hover:text-white/60 hover:bg-white/10'
              )}
            >
              <RefreshCw className="w-3 h-3" />
              <span>{isNowLocked ? 'unlock' : 'now'}</span>
            </button>
          </div>
        );
      })()}

      <main
        className={cn(
          'mx-auto flex-1 min-h-0',
          isSkinMode
            ? 'w-full overflow-hidden overscroll-none p-0'
            : 'container px-0 sm:px-4 pt-4 pb-[calc(3.5rem+var(--safe-area-bottom,0px))] md:pb-8 overflow-y-auto',
        )}
      >
        <div className={cn(isSkinMode ? 'h-full min-h-0 w-full px-0 flex flex-col' : 'max-w-[1600px] mx-auto px-4 sm:px-4')}>
          {firestoreDisabled && (
            <Alert variant="destructive" className="mb-4">
              <AlertTitle>Sync is turned off</AlertTitle>
              <AlertDescription>
                Dashboard data won&apos;t load because Firestore sync is disabled. Toggle <strong>Sync</strong> in the
                header to re-enable data loading, then refresh.
              </AlertDescription>
            </Alert>
          )}

          {isLoggedIn && canEditDashboard && (
            <div className={cn('mb-3 flex justify-end gap-2', isSkinMode ? 'px-3 pt-2' : '')}>
              <Button
                asChild
                variant="outline"
                size="sm"
                className="bg-card/70 backdrop-blur border-border/60"
                title="Open the YouTube Timeline Miner"
              >
                <Link to="/timeline-miner">
                  <Clapperboard className="h-4 w-4" />
                  <span className="ml-1.5 hidden sm:inline">Timeline Miner</span>
                </Link>
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowAlignmentTool(true)}
                className="bg-card/70 backdrop-blur border-border/60"
              >
                Edit Interlocking
              </Button>
            </div>
          )}

          <section id="dashboard" className={cn(isSkinMode ? 'flex-1 min-h-0 h-full px-0' : 'px-0')}>{activeView ? (
              <Dashboard
                widgets={activeWidgets}
                showTopToolbar={isLoggedIn}
                onSignOut={isLoggedIn ? onSignOut : undefined}
                onRemoveWidget={handleRemoveWidget}
                onAddWidgetById={canEditDashboard ? handleAddWidget : undefined}
                addableWidgets={canEditDashboard ? addableWidgets : []}
                onSaveLayout={canEditDashboard ? handleSaveDashboard : undefined}
                storageKey={dashboardStorageKey}
                isEditable={canEditDashboard}
                mobilePagerSettingsHeader={inlineHeaderInSettingsPage ? mobilePagerSettingsHeader : undefined}
                onViewModeChange={setDashboardViewMode}
                currentGregorianDate={currentGregorianDate}
                onSyncStateChange={setWidgetSyncState}
                onSyncModesChange={onSyncModesChange}
                onToggleAllWidgetsRef={(fn) => { toggleAllWidgetsRef.current = fn; }}
                cycleRings={cycles}
                hiddenRings={hiddenRings}
                onToggleRing={onToggleRing}
                onRingsChange={onRingsChange}
                onRotationChange={onRotationChangeFromCycles}
                onSyncToNow={handleSyncToNow}
                cycleDisplayMode={cycleDisplayMode}
                onCycleDisplayModeChange={onCycleDisplayModeChange}
                dashboardViews={safeDashboardViews}
                activeViewId={activeViewId}
                onViewIdChange={setActiveViewId}
                onCreateView={handleCreateView}
                onDeleteView={handleDeleteView}
                navItems={navItems}
                onOpenTheme={canEditDashboard ? () => setShowRingThemeWizard(true) : undefined}
                isNowLocked={isNowLocked}
              />
            ) : (
              <div className="rounded-2xl border border-border/60 bg-card/60 backdrop-blur p-8 text-center space-y-3">
                <p className="text-sm uppercase tracking-[0.3em] text-muted-foreground">No saved dashboards</p>
                <h2 className="text-xl font-display font-semibold">Create your first dashboard view.</h2>
                <p className="text-sm text-muted-foreground">
                  Save a new view to start customizing your dashboard layout.
                </p>
                <Button type="button" variant="outline" size="sm" onClick={handleCreateView}>
                  Create dashboard view
                </Button>
              </div>
            )}
          </section>
        </div>
      </main>

      {canEditDashboard && (
        <CycleWizard
          open={showCycleWizard}
          onClose={onCloseCycleWizard}
          onCreateCycle={onCreateCycle}
          onUpdateCycle={onUpdateCycle}
          onDeleteCycle={onDeleteCycle}
          editingCycle={editingCycle}
          dayOfYear={dayOfYear}
          allCycles={cycles}
          onImportCycles={onImportCycles}
        />
      )}

      <AlignmentTool
        open={showAlignmentTool}
        onClose={() => {
          setShowAlignmentTool(false);
          setAlignmentInitialCycleId(undefined);
        }}
        cycles={cycles}
        onAlign={onAlign}
        onApplyAlignment={onApplyAlignment}
        alignments={alignments}
        initialSourceCycleId={alignmentInitialCycleId}
        timelineDate={currentGregorianDate}
      />

      {canEditDashboard && (
        <RingThemeWizard
          open={showRingThemeWizard}
          onClose={() => setShowRingThemeWizard(false)}
          themes={ringThemeState.themes}
          activeThemeId={ringThemeState.activeThemeId}
          rings={cycles}
          onSaveTheme={onSaveTheme}
          onDeleteTheme={onDeleteTheme}
          onApplyThemeToRing={onApplyThemeToRing}
          onApplyThemeLayout={onApplyThemeLayout}
          onApplyThemeOrder={onApplyThemeOrder}
        />
      )}
    </div>
  );
}
